import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchCollection, fetchCategories } from "../services/api";
import type { Product, ProductCollection, Category } from "../utiles/types";
import SkeletonLoader from "../utiles/Skeletons/SkeletonLoader";
import { AiOutlineLeft, AiOutlinePlus, AiOutlineRight, AiOutlineFilter } from "react-icons/ai";
import { MdClose } from "react-icons/md";
import ProductCard from "../utiles/Cards/ProductCard";

// Constants
const PRODUCTS_PER_PAGE = 20;
const PRICE_RANGES = ["Low to High", "High to Low"] as const;

// Types
type FilterState = {
  sizes: string[];
  price: string | null;
  categories: string[];
};

const FilterSection = ({
  title,
  isOpen,
  onToggle,
  children,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) => (
  <div className="mb-6 border-b border-gray-200 pb-4">
    <div
      className="flex items-center justify-between cursor-pointer group"
      onClick={onToggle}
    >
      <span className="text-sm font-bold tracking-wider uppercase text-gray-900 group-hover:text-gray-600 transition-colors">{title}</span>
      <AiOutlinePlus className={`text-sm transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`} />
    </div>
    <div className={`mt-4 space-y-2 overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
      {children}
    </div>
  </div>
);

const Collection: React.FC = () => {
  const { category = "all" } = useParams<{ category?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [currentPage, setCurrentPage] = useState(() =>
    Number(searchParams.get("page")) || 1
  );

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    setSearchParams({ ...Object.fromEntries(searchParams), page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    sizes: [],
    price: null,
    categories: [],
  });

  // Default expanded for better UX
  const [expandedSections, setExpandedSections] = useState({
    size: true,
    price: true,
    category: true,
  });

  const [wishlist, setWishlist] = useState<Product[]>(() =>
    JSON.parse(localStorage.getItem("wishlist") || "[]")
  );
  const [products, setProducts] = useState<ProductCollection[]>([]);
  const [categoriesList, setCategoriesList] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [category]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [productsData, categoriesData] = await Promise.all([
          fetchCollection(),
          fetchCategories()
        ]);
        setProducts(Array.isArray(productsData) ? productsData.map(formatProduct) : []);
        setCategoriesList(categoriesData || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    setFilters({ sizes: [], price: null, categories: [] });
    setCurrentPage(1);
  }, [category]);

  const processedProducts = useMemo(() => {
    let filtered = products.filter((product) => {
      const trimmedProductCategory = product.category.trim().toLowerCase();
      const trimmedSelectedCategory = category?.trim().toLowerCase();

      const categoryMatch =
        trimmedSelectedCategory === "all" ||
        trimmedSelectedCategory === "collection" ||
        trimmedProductCategory === trimmedSelectedCategory;

      const sizeMatch =
        filters.sizes.length === 0 ||
        product.sizes?.some(({ size }) => filters.sizes.includes(size));

      // Case-insensitive check for category filters
      const categoryFilterMatch =
        filters.categories.length === 0 ||
        filters.categories.some(c => c.toLowerCase() === trimmedProductCategory);

      return categoryMatch && sizeMatch && categoryFilterMatch;
    });

    if (filters.price) {
      filtered.sort((a, b) => {
        return filters.price === "High to Low"
          ? b.price - a.price
          : a.price - b.price;
      });
    }

    return filtered;
  }, [products, category, filters]);

  const currentProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * PRODUCTS_PER_PAGE;
    return processedProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [processedProducts, currentPage]);

  const totalPages = Math.ceil(processedProducts.length / PRODUCTS_PER_PAGE);

  const handleNavigate = useCallback(
    (id: string) => {
      navigate(`/Product/${id}`);
    },
    [navigate]
  );

  const toggleWishlist = (product: Product) => {
    setWishlist((prevWishlist: Product[]) => {
      const exists = prevWishlist.find((item) => item.id === product.id);
      let newWishlist;

      if (exists) {
        newWishlist = prevWishlist.filter((item) => item.id !== product.id);
      } else {
        newWishlist = [...prevWishlist, product];
      }

      localStorage.setItem("wishlist", JSON.stringify(newWishlist));
      return newWishlist;
    });
  };

  const updateFilters = useCallback((key: keyof FilterState, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  if (isLoading) return <SkeletonLoader />;

  return (
    <div className="w-full min-h-screen bg-white text-black font-sans">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <header className="w-full max-w-[1920px] mx-auto flex justify-between items-center px-4 md:px-8 py-4">
          <div className="w-24"></div> {/* Spacer for center alignment */}

          <h1 className="text-2xl md:text-3xl lg:text-4xl font-light tracking-[0.2em] font-montserrat uppercase text-center">
            Collections
          </h1>

          <div className="w-24 flex justify-end">
            <button
              className="flex items-center space-x-2 text-sm font-medium uppercase tracking-wider hover:opacity-60 transition-opacity"
              onClick={() => setSidebarOpen(true)}
            >
              <span>Filter</span>
              <AiOutlineFilter className="text-lg" />
            </button>
          </div>
        </header>
      </div>

      {/* Main Content */}
      <div className="max-w-[1920px] mx-auto px-2 sm:px-4 md:px-8 pb-16 pt-8">

        {/* Results Info - Optional */}
        <div className="mb-6 flex justify-between items-center text-xs text-gray-500 uppercase tracking-widest px-2">
          <span>{processedProducts.length} Products</span>
          {/* Can add sort dropdown here if we want independent sort */}
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-x-2 gap-y-8 md:gap-x-6 md:gap-y-12">
          {currentProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              handleNavigate={handleNavigate}
              isWishlisted={wishlist.includes(product)}
              onToggleWishlist={toggleWishlist}
            />
          ))}
        </div>

        {currentProducts.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <h2 className="text-xl text-gray-400 font-light">No products found.</h2>
            <button
              onClick={() => setFilters({ sizes: [], price: null, categories: [] })}
              className="mt-4 text-sm underline underline-offset-4 hover:text-gray-600"
            >
              Clear all filters
            </button>
          </div>
        )}

        {/* Pagination */
          totalPages > 1 && (
            <div className="flex justify-center items-center mt-16 space-x-6">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 hover:border-black disabled:opacity-30 disabled:border-gray-200 disabled:cursor-not-allowed transition-colors"
              >
                <AiOutlineLeft />
              </button>
              <span className="text-sm font-medium tracking-widest">
                PAGE {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 hover:border-black disabled:opacity-30 disabled:border-gray-200 disabled:cursor-not-allowed transition-colors"
              >
                <AiOutlineRight />
              </button>
            </div>
          )}
      </div>

      {/* Filters Sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-500"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar Content */}
      <div
        className={`fixed top-0 left-0 h-full w-[320px] bg-white z-50 transform transition-transform duration-500 ease-out shadow-2xl ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="h-full flex flex-col p-8">
          <div className="flex justify-between items-center mb-10">
            <span className="text-xl font-light tracking-[0.2em] uppercase">Filters</span>
            <MdClose
              className="text-2xl cursor-pointer hover:rotate-90 transition-transform duration-300"
              onClick={() => setSidebarOpen(false)}
            />
          </div>

          <div className="flex-grow overflow-y-auto no-scrollbar pr-2">

            {/* Sort */}
            <FilterSection
              title="Sort By"
              isOpen={expandedSections.price}
              onToggle={() =>
                setExpandedSections((prev) => ({ ...prev, price: !prev.price }))
              }
            >
              {PRICE_RANGES.map((option) => (
                <div
                  key={option}
                  className="flex items-center space-x-3 cursor-pointer group py-1"
                  onClick={() => updateFilters("price", option)}
                >
                  <div className={`w-4 h-4 border rounded-full flex items-center justify-center transition-colors ${filters.price === option ? "border-black" : "border-gray-300 group-hover:border-gray-400"}`}>
                    {filters.price === option && <div className="w-2 h-2 bg-black rounded-full" />}
                  </div>
                  <span className="text-sm text-gray-600 group-hover:text-black">{option}</span>
                </div>
              ))}
            </FilterSection>

            {/* Category */}
            <FilterSection
              title="Category"
              isOpen={expandedSections.category}
              onToggle={() =>
                setExpandedSections((prev) => ({ ...prev, category: !prev.category }))
              }
            >
              {categoriesList.map((cat) => (
                <div
                  key={cat.id}
                  className="flex items-center space-x-3 cursor-pointer group py-1"
                  onClick={() => {
                    const isSelected = filters.categories.includes(cat.name);
                    const newCategories = isSelected
                      ? filters.categories.filter((c) => c !== cat.name)
                      : [...filters.categories, cat.name];
                    updateFilters("categories", newCategories);
                  }}
                >
                  <div className={`w-4 h-4 border flex items-center justify-center transition-colors ${filters.categories.includes(cat.name) ? "bg-black border-black" : "border-gray-300 group-hover:border-gray-400"}`}>
                    {filters.categories.includes(cat.name) && <span className="text-white text-xs">✓</span>}
                  </div>
                  <span className="text-sm text-gray-600 group-hover:text-black capitalize">{cat.name.replace("-", " ")}</span>
                </div>
              ))}
            </FilterSection>
          </div>

          {/* Sidebar Footer Actions */}
          <div className="mt-8 space-y-3 pt-6 border-t border-gray-100">
            <button
              className="w-full py-4 bg-black text-white text-sm font-medium tracking-widest uppercase hover:bg-gray-800 transition-colors"
              onClick={() => setSidebarOpen(false)}
            >
              View Results
            </button>

            <button
              className="w-full py-2 text-xs font-medium tracking-widest uppercase text-gray-500 hover:text-red-500 transition-colors"
              onClick={() => {
                setFilters({ sizes: [], price: null, categories: [] });
              }}
            >
              Clear All Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Utility functions
const formatProduct = (item: any): ProductCollection => ({
  id: item.id,
  name: item.name,
  price: item.price,
  category: item.category,
  image: item.image,
  description: item.description || "",
  sizes:
    item.sizes?.map((size: any) => ({
      id: size.id,
      size: size.size,
      quantity: size.quantity,
    })) || [],
  rating: item.rating || 0,
});

export default Collection;
